"use client";

import * as Tabs from "@radix-ui/react-tabs";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { use, useState, useTransition, type ReactNode } from "react";
import { SRCImage } from "react-datocms";
import { groupBy } from "remeda";

import type { CartTrip } from "@trip/common";
import { type RoomTier } from "@trip/constants";

import { CheckmarkFilledIcon } from "~/components/icons/checkmark-filled";
import { ExternalLinkIcon } from "~/components/icons/external-link";
import { LoadingSpinner } from "~/components/loading-spinner";
import { H3, Large } from "~/components/typography";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { useTrack } from "~/lib/use-track";
import { useRouter } from "~/navigation";
import { trpcReact } from "~/trpc/client";

import {
  TripContext,
  type DatoTripHotel,
  type DatoTripLocation,
} from "../_lib/trip-context";

export type Hotel = NonNullable<CartTrip["destinations"][number]["hotel"]>;

function HotelTier({
  hotels,
  label,
  value,
  isPending,
  onHover,
}: {
  hotels: DatoTripHotel[];
  label: ReactNode;
  value: DatoTripHotel | null;
  isPending: boolean;
  onHover: (hotel: DatoTripHotel) => void;
}) {
  const t = useTranslations("TripDetails");
  return (
    <div>
      <Large
        className="mb-2 mt-1 text-center sm:mb-4 sm:mt-2 sm:text-left"
        asChild
      >
        <div>{label}</div>
      </Large>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-3">
        {hotels.map((hotel) => {
          const isSelected = value?.id === hotel.id;
          const imageData = hotel.gallery[0]?.responsiveImage;
          return (
            <Tabs.Trigger
              className={`shadow-border data-active:shadow-xl group relative flex flex-col justify-start rounded-xl bg-white p-1.5 ring-2 transition-shadow hover:shadow-xl ${isSelected ? "ring-prune-500" : "ring-white"}`}
              value={hotel.id}
              key={hotel.id}
              onMouseEnter={() => {
                onHover(hotel);
              }}
            >
              {isSelected ? (
                <div
                  className={`bg-prune-500 absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-xs font-medium text-white`}
                >
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    {isPending ? (
                      <LoadingSpinner className="size-4" fill="white" />
                    ) : (
                      ""
                    )}
                  </div>
                  <span className={isPending ? "invisible" : undefined}>
                    {t("selected")}
                  </span>
                </div>
              ) : null}
              <div>
                {imageData ? (
                  <SRCImage imgClassName="rounded-lg mb-2" data={imageData} />
                ) : null}
                <div className="group-data-active:font-medium line-clamp-2 text-xs leading-tight">
                  {hotel.name}
                </div>
              </div>
            </Tabs.Trigger>
          );
        })}
      </div>
    </div>
  );
}

export function HotelPicker({
  location,
  destinationId,
  value,
  roomTier,
  tierRates,
}: {
  location: DatoTripLocation;
  destinationId: string;
  value: DatoTripHotel | null;
  roomTier: RoomTier;
  tierRates: { luxury: number; comfort: number };
}) {
  const track = useTrack();
  const router = useRouter();
  const formatCurrency = useCurrencyFormatter();
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("TripDetails");
  const trip = use(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const { datoTrip, lineItem } = trip;

  const datoTripActionMutation = trpcReact.cart.datoTripAction.useMutation();
  const cartTripActionMutation = trpcReact.cart.cartTripAction.useMutation();

  const isDatoTrip = lineItem === null;
  const isCartTrip = lineItem !== null;
  const [active, setActive] = useState(value ?? null);
  const [hoveredHotel, setHoveredHotel] = useState<DatoTripHotel | null>(null);
  const hotelTierGroups = groupBy(location.hotels, (hotel) => hotel.tier);

  // Fallback to first comfort hotel (visually leftmost) when no selection and no hover
  const firstComfortHotel = hotelTierGroups["comfort"]?.[0] ?? null;
  const displayHotel =
    hoveredHotel ?? active ?? firstComfortHotel ?? location.hotels[0] ?? null;

  return (
    <div className="-mx-6 flex flex-col gap-4 rounded-2xl bg-neutral-200 p-3 sm:mx-0">
      <Tabs.Root
        className=""
        onValueChange={(value) => {
          const found = location.hotels.find(
            (datoHotel) => datoHotel.id === value,
          );
          if (found) {
            setActive(found);
            if (isDatoTrip) {
              // For DatoTrip, find the destination index and use dato action
              const destinationIndex = datoTrip.tripDestinations.findIndex(
                (dest) => dest.id === destinationId,
              );
              if (destinationIndex >= 0) {
                void datoTripActionMutation
                  .mutateAsync({
                    type: "UPDATE_DESTINATION_ACCOMMODATION",
                    datoTripId: datoTrip.id,
                    destinationIndex,
                    datoHotelId: found.id,
                    roomTier: found.tier as RoomTier,
                  })
                  .then(() => {
                    track({
                      name: "Change Trip Hotel",
                      datoHotelId: found.id,
                      datoTripId: datoTrip.id,
                      tier: found.tier as RoomTier,
                    });
                    startTransition(() => {
                      router.refresh();
                    });
                  });
              }
            } else if (isCartTrip) {
              // For CartTrip, find the destination by datoTripDestinationId in lineItem.destinations
              const cartDestination = lineItem.destinations.find(
                (dest) => dest.datoTripDestinationId === destinationId,
              );
              if (cartDestination) {
                void cartTripActionMutation
                  .mutateAsync({
                    type: "UPDATE_DESTINATION_ACCOMMODATION",
                    tripLineItemId: lineItem.id,
                    destinationId: cartDestination.id,
                    datoHotelId: found.id,
                    roomTier: found.tier as RoomTier,
                  })
                  .then(() => {
                    track({
                      name: "Change Trip Hotel",
                      datoHotelId: found.id,
                      datoTripId: datoTrip.id,
                      tier: found.tier as RoomTier,
                    });
                    startTransition(() => {
                      router.refresh();
                    });
                  });
              }
            }
          }
        }}
        value={active?.id}
      >
        <Tabs.List
          className="grid gap-2 sm:grid-cols-2 sm:gap-4 sm:px-3"
          onMouseLeave={() => {
            setHoveredHotel(null);
          }}
        >
          {hotelTierGroups["comfort"] ? (
            <HotelTier
              isPending={isPending}
              value={active}
              onHover={setHoveredHotel}
              label={
                <>
                  {t("comfort")}{" "}
                  <span className="font-normal text-neutral-500">
                    {t("includedInBasePrice")}
                  </span>
                </>
              }
              hotels={hotelTierGroups["comfort"]}
            />
          ) : null}
          {hotelTierGroups["luxury"] ? (
            <HotelTier
              isPending={isPending}
              value={active}
              onHover={setHoveredHotel}
              label={
                <>
                  {t("luxury")}{" "}
                  <span className="font-normal text-neutral-500">
                    {t("handpickedQualityStaysPlus")}
                    {formatCurrency(tierRates.luxury)}
                  </span>
                </>
              }
              hotels={hotelTierGroups["luxury"]}
            />
          ) : null}
        </Tabs.List>
      </Tabs.Root>
      <div className="grow rounded-xl bg-white p-6">
        <AnimatePresence mode="wait">
          {displayHotel ? (
            <motion.div
              className="space-y-6"
              key={displayHotel.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-[1.5fr_1fr]">
                <div>
                  <H3 className="mb-4 font-medium">{displayHotel.name}</H3>
                  <ul className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[auto_auto]">
                    {displayHotel.whatsIncluded.map(({ text }) => (
                      <li key={text} className="flex items-center gap-2">
                        <CheckmarkFilledIcon className="size-6 text-neutral-500" />{" "}
                        <div className="truncate">{text}</div>
                      </li>
                    ))}
                    {displayHotel.url ? (
                      <li>
                        <a
                          target="_blank"
                          href={displayHotel.url}
                          className="text-prune-500 inline-flex items-center gap-2"
                          rel="noreferrer"
                        >
                          {t("visitHotelWebsite")} <ExternalLinkIcon />
                        </a>
                      </li>
                    ) : null}
                  </ul>
                </div>
                <div className="flex flex-col items-end space-y-4">
                  {displayHotel.tier === "comfort" ? (
                    <p className="text-green hidden text-right font-medium sm:block">
                      {roomTier === "luxury" ? (
                        <>
                          {t("saveByStayingHereInstead", {
                            amount: formatCurrency(tierRates.luxury),
                            hotelName: displayHotel.name,
                          })}
                        </>
                      ) : (
                        t("includedInTotalPrice")
                      )}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex w-full flex-col gap-2 sm:grid sm:grid-cols-3 sm:gap-6">
                {displayHotel.gallery.map(({ id, responsiveImage }) => (
                  <SRCImage
                    imgClassName="rounded-lg"
                    data={responsiveImage}
                    key={id}
                  />
                ))}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
